'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Link } from '@sola/ark-core/atoms/link/link.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { articlesUrlBase } from '@sola/contentful-model/lib/routes.js';
import {
  ArticleThemesEnum,
  ArticleTopicsEnum,
  ArticleTypesEnum,
} from '@sola/contentful-model/lib/taxonomy-values.js';
import { extractArgsSearchResult } from '@sola/skippy-search-sdk/lib/logic.js';
import { useListArticlesQuery } from '@sola/skippy-search-sdk/lib/queries.js';
import {
  SearchResultProps,
  SearchResultsInput,
} from '@sola/skippy-search-sdk/lib/schema.js';
import { trackSelectContent } from '@sola/tracking/components/datalayer.js';

import {
  ArticleSortByEnum,
  useArticleQueryParams,
  usePaginationQueryParams,
} from '../../utils/query-params';
import { getBaseSizeProps } from '../../utils/text-utils';
import { CardList } from '../card-list/card-list';
import { ArticleCardSkeleton, Card } from '../card/card';
import { FeatureBase } from '../feature-base/feature-base';
import { Pagination } from '../pagination/pagination';

const queryClient = new QueryClient();

export const ArticleListProps = z.object({
  seoLevel: z.number().int().optional(),
  articleType: ArticleTypesEnum.optional(),
  country: z.string().optional(),
  theme: z.string().optional(),
  topic: z.string().optional(),
  limit: z.number().int().optional(),
  onArticleLoad: z
    .function({
      input: [
        z.object({
          totalCount: z.number(),
        }),
      ],
    })
    .optional(),
  variant: z.enum(['default', 'related']).optional(),
  excludeContentfulEntryID: z.string().optional(),
  hidePagination: z.boolean().optional(),
});
export type ArticleListProps = z.infer<typeof ArticleListProps>;

const ArticleListSkeleton: React.FC<{
  itemCount?: number;
  columns?: number;
  columnsMd?: number;
  columnsLg?: number;
}> = ({ itemCount = 12, columns = 1, columnsMd = 3, columnsLg = 4 }) => {
  return (
    <CardList
      columns={columns}
      columnsMd={columnsMd}
      columnsLg={columnsLg}
      classNameGap="gap-y-roomy-xs gap-x-comfy-xl"
    >
      {Array.from({ length: itemCount }, (_, index) => (
        <ArticleCardSkeleton key={`loading-article-skeleton-${index}`} />
      ))}
    </CardList>
  );
};

const InternalArticleList: React.FC<ArticleListProps> = ({
  seoLevel = 3,
  country,
  theme,
  topic,
  onArticleLoad,
  articleType,
  excludeContentfulEntryID,
  variant = 'default',
  limit,
  hidePagination = false,
}) => {
  const { t } = useTranslation(['articles', 'skippy']);

  const { articleFilters, setArticleFilters } = useArticleQueryParams({
    perPage: limit,
  });
  const { currentPageWithoutScroll, setCurrentPageWithoutScroll } =
    usePaginationQueryParams();

  const [itemOffset, setItemOffset] = useState<number>(0);
  const sortBy = articleFilters.sortby || 'desc';

  const limitValue =
    (!hidePagination && articleFilters.limit
      ? articleFilters.limit
      : undefined) || limit;

  const perPageValue = limitValue || 12;
  const offsetValue = limitValue || 0;

  const columnLayout = {
    columns: 1,
    columnsMd: Math.min(articleFilters.limit, 3),
    columnsLg:
      variant === 'related'
        ? Math.min(articleFilters.limit, 3)
        : Math.min(articleFilters.limit, 4),
  };

  const sortByLabels = {
    featured: {
      text: t('articles:sort.featured'),
      value: ArticleSortByEnum.enum.promotedContent,
    },
    descending: {
      text: t('articles:sort.newest'),
      value: ArticleSortByEnum.enum.desc,
    },
    ascending: {
      text: t('articles:sort.oldest'),
      value: ArticleSortByEnum.enum.asc,
    },
  } as const;

  let sortDir: 'asc' | 'desc' = 'desc';
  if (articleFilters.sortby === sortByLabels.ascending.value) {
    sortDir = 'asc';
  }

  const filterString = {
    'taxonomy.articleType.keyword': articleType || undefined,
    'taxonomy.articleTopics.keyword':
      topic || articleFilters.topic || undefined,
    'taxonomy.articleThemes.keyword':
      theme || articleFilters.theme || undefined,
    'taxonomy.programCountry.keyword':
      country || articleFilters.country || undefined,
  };

  const mustNotString = excludeContentfulEntryID
    ? {
        'contentfulEntryID.keyword': excludeContentfulEntryID,
      }
    : {};

  const isFeaturedSort = articleFilters.sortby === sortByLabels.featured.value;

  const queryConfigs = {
    featured: {
      should: JSON.stringify({
        'taxonomy.other.keyword': 'promotedContent',
      }),
      shouldStrictness: 0,
      sort: 'datePublished',
      sortDir: 'desc',
    },
    default: {
      sort: 'datePublished',
      sortDir,
    },
  } as const;

  const queryConfig = queryConfigs[
    isFeaturedSort ? 'featured' : 'default'
  ] as SearchResultsInput;

  if (variant === 'related') {
    queryConfig.should = JSON.stringify(filterString);
    queryConfig.shouldStrictness = 0;
  } else {
    queryConfig.filters = JSON.stringify(filterString);
  }
  const articleQuery = useListArticlesQuery({
    ...queryConfig,
    perPage: articleFilters.limit,
    offset:
      currentPageWithoutScroll * Number(articleFilters.limit) || itemOffset,
    must_not: JSON.stringify(mustNotString),
  });

  const { data: articleData, isLoading, isError } = articleQuery;
  const articles = articleData?.items;
  const totalCount = articleData?.totalCount || 0;
  const shouldShowPagination = variant === 'default' && !hidePagination;

  const getEyebrow = (result: SearchResultProps) => {
    if (!articleType && result.taxonomyArticleType) {
      return t(
        `articles:taxonomy.articleTypes.${result.taxonomyArticleType as ArticleTypesEnum}.singularName`,
      );
    }
    if (result.taxonomyTopic) {
      return t(
        `articles:taxonomy.articleTopics.${result.taxonomyTopic[0] as ArticleTopicsEnum}.name`,
      );
    }
    if (result.taxonomyArticleTheme) {
      return t(
        `articles:taxonomy.articleThemes.${result.taxonomyArticleTheme[0] as ArticleThemesEnum}.name`,
      );
    }
    if (result.taxonomyArticleType) {
      return t(
        `articles:taxonomy.articleTypes.${result.taxonomyArticleType as ArticleTypesEnum}.singularName`,
      );
    }
    return undefined;
  };

  const handlePageChange = (page: number) => {
    setCurrentPageWithoutScroll(page);
    setItemOffset(page * Number(articleFilters.limit));
  };

  useEffect(() => {
    if (limit) {
      setArticleFilters((prev) => ({ ...prev, limit }));
    }
  }, [limit]);

  useEffect(() => {
    setItemOffset(Number(articleFilters.limit) * currentPageWithoutScroll || 0);
  }, [offsetValue, currentPageWithoutScroll]);

  useEffect(() => {
    if (onArticleLoad && !isLoading) {
      onArticleLoad({ totalCount });
    }
  }, [totalCount, isLoading, onArticleLoad]);

  if (isLoading) {
    return (
      <ArticleListSkeleton itemCount={articleFilters.limit} {...columnLayout} />
    );
  }

  if (isError) {
    return (
      <div className="ArticleList flex flex-col gap-comfy-sm">
        <div className="text-center p-comfy-xl">
          <Alert
            variant="negative"
            message={t(
              'skippy:articles.errorLoading',
              'Error loading articles. Please try again.',
            )}
          />
        </div>
      </div>
    );
  }

  if (!articles?.length) {
    return (
      <div className="ArticleList flex flex-col gap-comfy-sm">
        <FeatureBase
          title={t('articles:noResultsTitle')}
          body={
            <Trans
              i18nKey="articles:noResultsBody"
              components={{
                1: <p />,
                2: (
                  <Link
                    href={`${articlesUrlBase[articleType as ArticleTypesEnum]}?tab=theme`}
                  />
                ),
                3: (
                  <Link
                    href={`${articlesUrlBase[articleType as ArticleTypesEnum]}?tab=search`}
                  />
                ),
              }}
            />
          }
        />
      </div>
    );
  }

  const uniqueArticles = articles.filter(
    (a, i, arr) =>
      arr.findIndex((b) => b.contentfulEntryID === a.contentfulEntryID) === i,
  );

  return (
    <div className="ArticleList flex flex-col gap-comfy-sm">
      <CardList {...columnLayout} classNameGap="gap-y-roomy-xs gap-x-comfy-xl">
        {uniqueArticles?.map((article) => {
          const cardProps = extractArgsSearchResult(article);
          return (
            <Card
              key={article.contentfulEntryID}
              {...getBaseSizeProps(4)}
              seoLevel={seoLevel}
              title={article.pageTitle}
              subtitle={getEyebrow(cardProps)}
              media={cardProps.media}
              body={<p>{article.summary}</p>}
              primaryCTA={
                article.path
                  ? {
                      link: article.path,
                      title: 'Read more',
                    }
                  : undefined
              }
              variant="post"
              hasPlaceholder
              alt
              onClick={() =>
                trackSelectContent(
                  article.contentfulEntryID!,
                  article.pageTitle!,
                  article.contentfulType!,
                  [
                    ...(article.taxonomy?.articleTopics ?? []),
                    ...(article.taxonomy?.articleThemes ?? []),
                  ],
                )
              }
            />
          );
        }) || []}
      </CardList>
      {shouldShowPagination && articleData && articleData.totalCount && (
        <Pagination
          itemsPerPage={Number(articleFilters.limit)}
          totalItems={articleData.totalCount}
          onPageChange={(page) => handlePageChange(page)}
        />
      )}
    </div>
  );
};

export const ArticleList: React.FC<ArticleListProps> = (props) => {
  return (
    <Suspense fallback={<ArticleListSkeleton />}>
      <QueryClientProvider client={queryClient}>
        <InternalArticleList {...props} />
      </QueryClientProvider>
    </Suspense>
  );
};
